import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import { getBrandBaseCurrency } from '@utils/get-brand-base-currency';
import { getBrandLoginRedirect } from '@utils/get-brand-redirect.ts';

import { getUrlToBackendApi, request } from '@shared/services/api';

import { config } from '@/config.ts';

import { LoginResponse } from './use-mutation-login';
import { pickAccountCurrency } from './utils/pick-account-currency';
import { GET_ME_QUERY_KEY } from '../user/use-query-me';

const REQUEST_URL = '/login_by_site';

const MUTATION_KEY = ['auth', 'two-fa', 'login'];

export const useTwoFaAuthorization = (shouldReload?: boolean) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: MUTATION_KEY,
		mutationFn: async ({ code, twoFASessionKey }: { code: string; twoFASessionKey: string | null }) => {
			try {
				const externalResponse = await axios.post(
					`${config.GR8_URL}/two-fa/login`,
					{
						twoFASessionKey: twoFASessionKey,
						twoFACode: code
					},
					{
						headers: {
							'X-API-KEY': config.API_KEY,
							'Content-Type': 'application/json',
							'X-TwoFA-Ver': 1
						}
					}
				);

				if (externalResponse?.data?.accountInfo) {
					const { number, phone } = externalResponse.data.accountInfo;
					const currency = pickAccountCurrency(externalResponse.data.accountInfo) ?? getBrandBaseCurrency();

					const response = await request<LoginResponse>({
						method: 'post',
						url: getUrlToBackendApi(REQUEST_URL).href,
						data: {
							pm_id: number,
							currency,
							phone: phone
						}
					});

					if (response?.meta?.access_token && response?.meta?.refresh_token) {
						localStorage.setItem('auth_token', response.meta.access_token);
						localStorage.setItem('refresh_token', response.meta.refresh_token);
					}

					return response;
				} else {
					throw new Error('Account info-page not found in external response');
				}
			} catch (error) {
				console.error('Login mutation error:', error);
				throw error;
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: GET_ME_QUERY_KEY });

			shouldReload && navigate(getBrandLoginRedirect(), { replace: true });
		},
		onError: error => {
			console.error('Error during login mutation:', error);
		}
	});
};
